import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api, { patientApi, taskApi, appointmentApi, formAssignmentApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Home, Users, CheckCircle, AlertTriangle, Clock, ClipboardCheck,
  Activity, ArrowRight, Calendar, UserCheck, FileText, TrendingUp,
  BadgeAlert, Clipboard, ShieldCheck, Heart, RefreshCw, Radio, Wifi, WifiOff
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const COLORS = {
  completed: '#166534',
  inProgress: '#0E7490',
  overdue: '#991B1B',
  pending: '#CA8A04',
  missed: '#991B1B',
};

export default function HomeCareDashboard() {
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [appts, setAppts] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [hha, setHha] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const [pRes, tRes, aRes, fRes, actRes, hhaRes] = await Promise.all([
        patientApi.list({ limit: 1000 }).catch(() => ({ data: [] })),
        taskApi.list().catch(() => ({ data: { tasks: [] } })),
        appointmentApi.list({ date: today }).catch(() => ({ data: { appointments: [] } })),
        formAssignmentApi.list({}).catch(() => ({ data: { assignments: [] } })),
        api.get('/auth/activity?limit=500').catch(() => ({ data: { logs: [] } })),
        api.get('/home-care/hha/overview').catch(() => ({ data: null })),
      ]);
      setPatients(Array.isArray(pRes.data) ? pRes.data : (pRes.data.patients || []));
      setTasks(tRes.data.tasks || []);
      setAppts(aRes.data.appointments || []);
      setAssignments(fRes.data.assignments || []);
      setActivity(actRes.data.logs || []);
      setHha(hhaRes.data || null);
    } finally {
      setLoading(false);
    }
  };

  const handleSyncNow = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/admin/hha/sync');
      const mode = res.data?.mode || 'unknown';
      const counts = res.data?.counts || {};
      toast.success(
        `HHA sync complete (${mode}) — ${counts.hha_patients || 0} patients, ${counts.hha_visits || 0} visits`
      );
      // Refresh overview
      const ov = await api.get('/home-care/hha/overview').catch(() => ({ data: null }));
      setHha(ov.data || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed — you may not have super_admin rights');
    } finally {
      setSyncing(false);
    }
  };

  // ------- Home-care metrics -------
  const activeCensus = useMemo(() => patients.filter(p => p.status !== 'discharged' && p.status !== 'inactive').length, [patients]);
  const newAdmissions30d = useMemo(() => {
    const cutoff = new Date(Date.now() - 30 * 24 * 3600 * 1000);
    return patients.filter(p => p.created_at && new Date(p.created_at) >= cutoff).length;
  }, [patients]);
  const dischargedPct = useMemo(() => {
    const total = patients.length || 1;
    const discharged = patients.filter(p => p.status === 'discharged').length;
    return Math.round((discharged / total) * 100);
  }, [patients]);

  // Visit / appointment metrics (today)
  const visitsToday = appts.length;
  const visitsCompleted = useMemo(() => appts.filter(a => a.status === 'completed').length, [appts]);
  const visitsMissed = useMemo(() => appts.filter(a => a.status === 'no_show' || a.status === 'cancelled').length, [appts]);
  const visitCompletionRate = visitsToday ? Math.round((visitsCompleted / visitsToday) * 100) : 0;

  // Plan of care (form assignments) — OASIS / Plan of Care / Re-certification are the big 3 in home care
  const overdueAssessments = useMemo(() => {
    const now = Date.now();
    return assignments.filter(a => a.status !== 'completed' && a.due_date && new Date(a.due_date).getTime() < now);
  }, [assignments]);
  const dueThisWeek = useMemo(() => {
    const now = Date.now();
    const wk = now + 7 * 24 * 3600 * 1000;
    return assignments.filter(a =>
      a.status !== 'completed' &&
      a.due_date && new Date(a.due_date).getTime() >= now && new Date(a.due_date).getTime() < wk
    );
  }, [assignments]);

  // Caregiver compliance (tasks completion rate by assignee)
  const caregiverStats = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const k = t.assigned_to_name || t.assigned_to || 'Unassigned';
      if (!map[k]) map[k] = { name: k, total: 0, completed: 0 };
      map[k].total += 1;
      if (t.status === 'completed') map[k].completed += 1;
    });
    return Object.values(map)
      .map(r => ({ ...r, rate: r.total ? Math.round((r.completed / r.total) * 100) : 0 }))
      .sort((a, b) => b.rate - a.rate)
      .slice(0, 8);
  }, [tasks]);

  // Audit / EVV-style verification — how much PHI was accessed last 7 days
  const evvTimeline = useMemo(() => {
    const days = [];
    const map = {};
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      days.push({ key, label: d.toLocaleDateString('en-US', { weekday: 'short' }) });
      map[key] = { day: d.toLocaleDateString('en-US', { weekday: 'short' }), visits: 0, charting: 0 };
    }
    activity.forEach(log => {
      const day = (log.timestamp || '').slice(0, 10);
      if (map[day]) {
        if (log.resource_type === 'patient') map[day].visits += 1;
        if (log.resource_type === 'form_submission' || log.action === 'create') map[day].charting += 1;
      }
    });
    return days.map(d => map[d.key]);
  }, [activity]);

  // Compliance snapshot
  const totalAssessments = assignments.length;
  const completedAssessments = assignments.filter(a => a.status === 'completed').length;
  const assessmentCompletionRate = totalAssessments ? Math.round((completedAssessments / totalAssessments) * 100) : 0;

  const statusPie = useMemo(() => [
    { name: 'Completed', value: visitsCompleted, color: COLORS.completed },
    { name: 'In Progress', value: appts.filter(a => a.status === 'in_progress').length, color: COLORS.inProgress },
    { name: 'Pending', value: appts.filter(a => a.status === 'scheduled').length, color: COLORS.pending },
    { name: 'Missed', value: visitsMissed, color: COLORS.missed },
  ].filter(s => s.value > 0), [appts, visitsCompleted, visitsMissed]);

  if (loading) {
    return <div className="flex justify-center py-16" data-testid="home-care-loading"><div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-8" data-testid="home-care-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-[#0A2342] flex items-center gap-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
          <Home className="w-8 h-8 text-[#0E7490]" />
          Home Care Agency Dashboard
        </h1>
        <p className="text-[#64748B] mt-1">Operational snapshot — census, visits, assessments, and caregiver performance</p>
      </div>

      {/* HHA Exchange Live Sync section */}
      {hha && <HHAExchangeSection data={hha} onSyncNow={handleSyncNow} syncing={syncing} />}

      {/* KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={<Users className="w-5 h-5 text-[#0E7490]" />} label="Active Census" value={activeCensus} sub={`${newAdmissions30d} new in last 30d`} testId="kpi-census" />
        <KPI icon={<Calendar className="w-5 h-5 text-[#1A56DB]" />} label="Today's Visits" value={`${visitsCompleted}/${visitsToday}`} sub={`${visitCompletionRate}% completion`} testId="kpi-visits" accent={visitCompletionRate >= 90 ? 'good' : visitCompletionRate >= 70 ? 'warn' : 'bad'} />
        <KPI icon={<BadgeAlert className="w-5 h-5 text-[#991B1B]" />} label="Overdue Assessments" value={overdueAssessments.length} sub={`${dueThisWeek.length} due this week`} testId="kpi-overdue" accent={overdueAssessments.length === 0 ? 'good' : 'bad'} />
        <KPI icon={<ClipboardCheck className="w-5 h-5 text-[#166534]" />} label="Assessment Compliance" value={`${assessmentCompletionRate}%`} sub={`${completedAssessments}/${totalAssessments} complete`} testId="kpi-compliance" accent={assessmentCompletionRate >= 90 ? 'good' : assessmentCompletionRate >= 75 ? 'warn' : 'bad'} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Visits pie chart */}
        <Card className="careiq-card" data-testid="visit-status-card">
          <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342]">Today's Visit Status</CardTitle></CardHeader>
          <CardContent>
            {statusPie.length === 0 ? (
              <div className="text-center py-10 text-sm text-[#94A3B8]">No visits scheduled today</div>
            ) : (
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={statusPie} cx="50%" cy="50%" innerRadius={40} outerRadius={80} paddingAngle={3} dataKey="value" label={(e) => `${e.name}: ${e.value}`}>
                      {statusPie.map((e, i) => <Cell key={i} fill={e.color} />)}
                    </Pie>
                    <Tooltip />
                    <Legend verticalAlign="bottom" iconSize={8} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Caregiver leaderboard */}
        <Card className="careiq-card lg:col-span-2" data-testid="caregiver-leaderboard-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#0E7490]" />
              Caregiver Compliance
            </CardTitle>
            <Link to="/tasks" className="text-xs text-[#0E7490] hover:underline flex items-center gap-1">View tasks <ArrowRight className="w-3 h-3" /></Link>
          </CardHeader>
          <CardContent>
            {caregiverStats.length === 0 ? (
              <div className="text-center py-10 text-sm text-[#94A3B8]">No task data yet</div>
            ) : (
              <div style={{ width: '100%', height: 220 }}>
                <ResponsiveContainer>
                  <BarChart data={caregiverStats} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#64748B" style={{ fontSize: 11 }} domain={[0, 100]} />
                    <YAxis type="category" dataKey="name" stroke="#64748B" style={{ fontSize: 11 }} width={100} />
                    <Tooltip formatter={(v, _, p) => [`${v}% (${p.payload.completed}/${p.payload.total})`, 'Completion']} />
                    <Bar dataKey="rate" fill="#0E7490" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* EVV-style activity timeline */}
      <Card className="careiq-card" data-testid="evv-timeline-card">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#0E7490]" />
            EVV Activity — Last 7 Days
          </CardTitle>
          <p className="text-xs text-[#64748B]">Patient record access + charting entries by day (Electronic Visit Verification proxy)</p>
        </CardHeader>
        <CardContent>
          <div style={{ width: '100%', height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={evvTimeline} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="day" stroke="#64748B" style={{ fontSize: 11 }} />
                <YAxis stroke="#64748B" style={{ fontSize: 11 }} allowDecimals={false} />
                <Tooltip />
                <Legend iconSize={8} />
                <Bar dataKey="visits" name="Patient record access" fill="#0E7490" radius={[4, 4, 0, 0]} />
                <Bar dataKey="charting" name="Charting / documentation" fill="#1A56DB" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Overdue Assessments list */}
      {overdueAssessments.length > 0 && (
        <Card className="careiq-card border-l-4 border-l-[#991B1B]" data-testid="overdue-assessments-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#991B1B] flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Overdue Assessments ({overdueAssessments.length})
            </CardTitle>
            <p className="text-xs text-[#64748B]">Plan-of-care re-certifications and OASIS assessments past their due date</p>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {overdueAssessments.slice(0, 10).map((a) => {
                const days = Math.floor((Date.now() - new Date(a.due_date).getTime()) / (1000 * 60 * 60 * 24));
                return (
                  <Link
                    key={a.id}
                    to={a.form_id ? `/forms/${a.form_id}/fill?patientId=${a.patient_id || ''}` : '/forms'}
                    className="flex items-center justify-between p-2 rounded border border-[#FECACA] bg-white hover:bg-[#FEF2F2] transition-colors"
                    data-testid={`overdue-${a.id}`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-[#0A2342] truncate">{a.form_name || 'Assessment'}</div>
                      <div className="text-[10px] text-[#64748B] truncate">{a.patient_name || 'Unassigned'}</div>
                    </div>
                    <div className="text-[10px] px-2 py-0.5 rounded-full bg-[#991B1B] text-white shrink-0">
                      {days} day{days === 1 ? '' : 's'} overdue
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// Small KPI card helper
function KPI({ icon, label, value, sub, accent, testId }) {
  const accentClass = accent === 'good' ? 'border-l-[#166534]' : accent === 'warn' ? 'border-l-[#CA8A04]' : accent === 'bad' ? 'border-l-[#991B1B]' : 'border-l-[#0E7490]';
  return (
    <Card className={`careiq-metric-card border-l-4 ${accentClass}`} data-testid={testId}>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#F1F5F9] rounded-lg flex items-center justify-center shrink-0">{icon}</div>
          <div className="min-w-0">
            <div className="text-2xl font-bold text-[#0A2342]">{value}</div>
            <div className="text-xs text-[#64748B] font-medium">{label}</div>
            {sub && <div className="text-[10px] text-[#94A3B8]">{sub}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------- HHA Exchange live-sync section ----------
function HHAExchangeSection({ data, onSyncNow, syncing }) {
  const last = data.last_sync;
  const kpis = data.kpis || {};
  const trend = data.visits_trend || [];
  const caregivers = data.top_caregivers || [];
  const mode = last?.mode || 'never';
  const isLive = mode === 'live';
  const isDemo = mode === 'demo' || mode === 'demo_fallback';
  const syncedAt = last?.finished_at ? new Date(last.finished_at) : null;
  const syncedAgo = syncedAt ? timeAgo(syncedAt) : 'never';

  const modeBadge = (() => {
    if (isLive) return { label: 'LIVE', bg: '#DCFCE7', fg: '#166534', icon: <Wifi className="w-3 h-3" /> };
    if (mode === 'demo_fallback') return { label: 'DEMO (API unreachable)', bg: '#FEF3C7', fg: '#92400E', icon: <WifiOff className="w-3 h-3" /> };
    if (mode === 'demo') return { label: 'DEMO', bg: '#E0E7FF', fg: '#3730A3', icon: <Radio className="w-3 h-3" /> };
    return { label: 'NEVER SYNCED', bg: '#FEE2E2', fg: '#991B1B', icon: <AlertTriangle className="w-3 h-3" /> };
  })();

  return (
    <div className="space-y-5" data-testid="hha-exchange-section">
      {/* Sync status banner */}
      <Card className="careiq-card border-l-4 border-l-[#0E7490]" data-testid="hha-sync-banner">
        <CardContent className="p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-lg bg-[#F0FDFA] flex items-center justify-center shrink-0">
              <Radio className="w-5 h-5 text-[#0E7490]" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-semibold text-[#0A2342]">HHA Exchange</span>
                <span
                  className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full"
                  style={{ background: modeBadge.bg, color: modeBadge.fg }}
                  data-testid="hha-sync-mode-badge"
                >
                  {modeBadge.icon}
                  {modeBadge.label}
                </span>
              </div>
              <div className="text-xs text-[#64748B] mt-0.5">
                Last sync: <span className="font-medium text-[#0A2342]" data-testid="hha-last-sync">{syncedAgo}</span>
                {last?.counts && (
                  <> — {last.counts.hha_patients || 0} patients, {last.counts.hha_visits || 0} visits, {last.counts.hha_evv_records || 0} EVV events</>
                )}
              </div>
              {last?.error && last.mode === 'demo_fallback' && (
                <div className="text-[10px] text-[#92400E] mt-1 truncate max-w-xl" title={last.error}>
                  Fallback reason: {last.error.slice(0, 120)}
                </div>
              )}
            </div>
          </div>
          <Button
            onClick={onSyncNow}
            disabled={syncing}
            className="careiq-btn-primary flex items-center gap-2"
            data-testid="hha-sync-now-btn"
          >
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing…' : 'Sync Now'}
          </Button>
        </CardContent>
      </Card>

      {/* HHA KPI Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          icon={<Users className="w-5 h-5 text-[#0E7490]" />}
          label="HHA Active Census"
          value={kpis.active_patients ?? 0}
          sub={`${kpis.total_patients ?? 0} total • ${kpis.on_hold ?? 0} on hold`}
          testId="hha-kpi-census"
        />
        <KPI
          icon={<Calendar className="w-5 h-5 text-[#1A56DB]" />}
          label="HHA Visits Today"
          value={`${kpis.completed_today ?? 0}/${kpis.visits_today ?? 0}`}
          sub={`${kpis.visit_completion_rate ?? 0}% completion • ${kpis.in_progress_today ?? 0} in progress`}
          testId="hha-kpi-visits"
          accent={(kpis.visit_completion_rate ?? 0) >= 85 ? 'good' : (kpis.visit_completion_rate ?? 0) >= 60 ? 'warn' : 'bad'}
        />
        <KPI
          icon={<ShieldCheck className="w-5 h-5 text-[#166534]" />}
          label="EVV Verification"
          value={`${kpis.evv_verification_rate ?? 0}%`}
          sub={`${kpis.evv_total ?? 0} events verified`}
          testId="hha-kpi-evv"
          accent={(kpis.evv_verification_rate ?? 0) >= 90 ? 'good' : (kpis.evv_verification_rate ?? 0) >= 75 ? 'warn' : 'bad'}
        />
        <KPI
          icon={<BadgeAlert className="w-5 h-5 text-[#CA8A04]" />}
          label="Auths Expiring ≤14d"
          value={kpis.auth_expiring_soon ?? 0}
          sub="Renewal risk"
          testId="hha-kpi-auth"
          accent={(kpis.auth_expiring_soon ?? 0) === 0 ? 'good' : 'warn'}
        />
      </div>

      {/* Visits trend + caregivers */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="careiq-card lg:col-span-2" data-testid="hha-visits-trend-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[#0E7490]" />
              HHA Visits — Last 7 Days
            </CardTitle>
            <p className="text-xs text-[#64748B]">Completed vs missed visits synced from HHA Exchange</p>
          </CardHeader>
          <CardContent>
            {trend.length === 0 ? (
              <div className="text-center py-10 text-sm text-[#94A3B8]">No visit data synced yet</div>
            ) : (
              <div style={{ width: '100%', height: 240 }}>
                <ResponsiveContainer>
                  <BarChart data={trend} margin={{ top: 5, right: 16, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="label" stroke="#64748B" style={{ fontSize: 11 }} />
                    <YAxis stroke="#64748B" style={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip />
                    <Legend iconSize={8} />
                    <Bar dataKey="completed" name="Completed" stackId="a" fill="#166534" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="missed" name="Missed" stackId="a" fill="#991B1B" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="careiq-card" data-testid="hha-caregivers-card">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-[#0E7490]" />
              Top Caregivers
            </CardTitle>
            <p className="text-xs text-[#64748B]">By completed HHA visits this week</p>
          </CardHeader>
          <CardContent>
            {caregivers.length === 0 ? (
              <div className="text-center py-10 text-sm text-[#94A3B8]">No caregiver data</div>
            ) : (
              <div className="space-y-2">
                {caregivers.map((c, i) => {
                  const max = caregivers[0]?.visits || 1;
                  const pct = Math.round((c.visits / max) * 100);
                  return (
                    <div key={c.name} className="text-xs" data-testid={`hha-caregiver-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-[#0A2342] truncate pr-2">{c.name}</span>
                        <span className="text-[#64748B] tabular-nums shrink-0">{c.visits}</span>
                      </div>
                      <div className="h-1.5 bg-[#F1F5F9] rounded-full overflow-hidden">
                        <div className="h-full bg-[#0E7490] rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function timeAgo(date) {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}
